import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft,
  Circle,
  Clock,
  AlertTriangle,
  CheckSquare,
  X,
  Trash2,
  Calendar,
  User,
  Tag,
  Crosshair,
  History,
  ArrowRight,
  Pencil,
  Check,
} from 'lucide-react';
import type { JSONContent } from '@tiptap/react';
import { DossierEditor } from '../components/editor';
import { apiGet, apiPost, apiPatch, apiDelete } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { LinkPicker } from '../components/LinkPicker';
import { DropdownPicker } from '../components/DropdownPicker';
import { sortPeopleWithSelf, displayName } from '../lib/people';
import { formatRelativeTime, useNow } from '../lib/relativeTime';

/* ── Types ─────────────────────────────────────────────── */

interface TaskDetail {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  due_date: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  source_meeting_id: string | null;
  parent_task_id: string | null;
  scope_ids: string[];
  tag_ids: string[];
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

interface HistoryEntry {
  id: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  changed_at: string;
}

interface TagItem {
  id: string;
  name: string;
  color: string;
}

interface PersonItem {
  id: string;
  name: string;
  is_self?: boolean;
}

interface ScopeItem {
  id: string;
  name: string;
  color: string | null;
}

/* ── Constants ─────────────────────────────────────────── */

const STATUS_OPTIONS = ['todo', 'in_progress', 'blocked', 'done', 'cancelled'];
const PRIORITY_OPTIONS = ['urgent', 'high', 'medium', 'low'];

const STATUS_CONFIG: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  todo: { icon: <Circle size={14} />, label: 'To Do', color: 'text-text-muted' },
  in_progress: { icon: <Clock size={14} />, label: 'In Progress', color: 'text-accent' },
  blocked: { icon: <AlertTriangle size={14} />, label: 'Blocked', color: 'text-danger' },
  done: { icon: <CheckSquare size={14} />, label: 'Done', color: 'text-success' },
  cancelled: { icon: <X size={14} />, label: 'Cancelled', color: 'text-text-muted' },
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
  urgent: { label: 'Urgent', color: 'text-danger' },
  high: { label: 'High', color: 'text-warning' },
  medium: { label: 'Medium', color: 'text-text-secondary' },
  low: { label: 'Low', color: 'text-text-muted' },
};

const HISTORY_ICONS: Record<string, React.ReactNode> = {
  status: <Circle size={12} />,
  priority: <AlertTriangle size={12} />,
  title: <Pencil size={12} />,
  due_date: <Calendar size={12} />,
  assignee_id: <User size={12} />,
};

const HISTORY_COLORS: Record<string, string> = {
  status: 'text-accent',
  priority: 'text-warning',
  title: 'text-text-secondary',
  due_date: 'text-text-muted',
  assignee_id: 'text-success',
};

/* ── Main Component ───────────────────────────────────── */

export function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  useNow(); // tick every 30s so relative timestamps stay fresh

  const [task, setTask] = useState<TaskDetail | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [tags, setTags] = useState<TagItem[]>([]);
  const [people, setPeople] = useState<PersonItem[]>([]);
  const [scopes, setScopes] = useState<ScopeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Inline editing states
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  const [showTagPicker, setShowTagPicker] = useState(false);
  const [showScopePicker, setShowScopePicker] = useState(false);

  /* ── Data Loading ───────────────────────────────────── */

  const loadTask = useCallback(async () => {
    if (!taskId) return;
    try {
      const [t, h, allTags, allPeople, allScopes] = await Promise.all([
        apiGet<TaskDetail>(`/tasks/${taskId}`),
        apiGet<HistoryEntry[]>(`/tasks/${taskId}/history`),
        apiGet<TagItem[]>('/tags'),
        apiGet<PersonItem[]>('/people'),
        apiGet<ScopeItem[]>('/scopes'),
      ]);
      setTask(t);
      setHistory(h);
      setTags(allTags);
      setPeople(allPeople);
      setScopes(allScopes);
    } catch (err) {
      console.error('Failed to load task:', err);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { loadTask(); }, [loadTask]);

  /* ── Field Patching ─────────────────────────────────── */

  async function patchTask(updates: Record<string, unknown>) {
    if (!taskId) return;
    setSaving(true);
    try {
      const updated = await apiPatch<TaskDetail>(`/tasks/${taskId}`, updates);
      setTask(updated);
      // Refresh history after a change
      const h = await apiGet<HistoryEntry[]>(`/tasks/${taskId}/history`);
      setHistory(h);
    } catch (err) {
      console.error('Failed to update task:', err);
      toast.error('Failed to update task');
    } finally {
      setSaving(false);
    }
  }

  /* ── Title Editing ──────────────────────────────────── */

  function startEditTitle() {
    if (!task) return;
    setTitleDraft(task.title);
    setEditingTitle(true);
    setTimeout(() => titleInputRef.current?.focus(), 0);
  }

  function saveTitle() {
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== task?.title) {
      patchTask({ title: trimmed });
    }
    setEditingTitle(false);
  }

  function cancelEditTitle() {
    setEditingTitle(false);
  }

  /* ── Description Autosave ───────────────────────────── */

  const descriptionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDescriptionUpdate = useCallback((content: JSONContent) => {
    if (descriptionTimerRef.current) clearTimeout(descriptionTimerRef.current);
    descriptionTimerRef.current = setTimeout(() => {
      patchTask({ description: JSON.stringify(content) });
    }, 1000);
  }, [taskId]);

  /* ── Tag / Scope Toggling ───────────────────────────── */

  function toggleTag(tagId: string) {
    if (!task) return;
    const newTagIds = task.tag_ids.includes(tagId)
      ? task.tag_ids.filter(id => id !== tagId)
      : [...task.tag_ids, tagId];
    patchTask({ tag_ids: newTagIds });
  }

  function toggleScope(scopeId: string) {
    if (!task) return;
    const newScopeIds = task.scope_ids.includes(scopeId)
      ? task.scope_ids.filter(id => id !== scopeId)
      : [...task.scope_ids, scopeId];
    patchTask({ scope_ids: newScopeIds });
  }

  async function createTag(name: string) {
    try {
      const created = await apiPost<TagItem>('/tags', { name });
      setTags(prev => [...prev, created]);
      return { id: created.id, label: created.name, color: created.color };
    } catch (err) {
      console.error('Failed to create tag:', err);
      toast.error('Failed to create tag');
    }
  }

  async function createScope(name: string) {
    try {
      const created = await apiPost<ScopeItem>('/scopes', { name });
      setScopes(prev => [...prev, created]);
      return { id: created.id, label: created.name, color: created.color ?? undefined };
    } catch (err) {
      console.error('Failed to create project:', err);
      toast.error('Failed to create project');
    }
  }

  /* ── Delete ─────────────────────────────────────────── */

  async function handleDelete() {
    if (!taskId) return;
    if (!window.confirm('Are you sure you want to delete this task? This cannot be undone.')) return;
    try {
      await apiDelete(`/tasks/${taskId}`);
      toast.success('Task deleted');
      navigate('/tasks');
    } catch (err) {
      console.error('Failed to delete task:', err);
      toast.error('Failed to delete task');
    }
  }

  /* ── Render ─────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <Link to="/tasks" className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors mb-4">
          <ChevronLeft size={16} /> Back to Tasks
        </Link>
        <div className="text-text-muted text-center py-16">Task not found.</div>
      </div>
      </div>
    );
  }

  const tagMap = new Map(tags.map(t => [t.id, t]));
  const scopeMap = new Map(scopes.map(s => [s.id, s]));
  const personMap = new Map(people.map(p => [p.id, p]));
  const statusCfg = STATUS_CONFIG[task.status] ?? STATUS_CONFIG.todo;
  const priorityCfg = PRIORITY_CONFIG[task.priority] ?? PRIORITY_CONFIG.medium;

  const noteContent: JSONContent | null = task.description
    ? (() => { try { return JSON.parse(task.description); } catch { return null; } })()
    : null;

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-4xl mx-auto">
      {/* Back nav */}
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/tasks"
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors"
        >
          <ChevronLeft size={16} /> Back to Tasks
        </Link>
        <div className="flex items-center gap-2">
          {saving && <span className="text-xs text-accent">Saving...</span>}
          <button
            onClick={handleDelete}
            className="flex items-center gap-1 px-2 py-1 text-xs text-text-muted hover:text-danger hover:bg-bg-hover rounded transition-colors"
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="mb-6">
        {editingTitle ? (
          <div className="flex items-center gap-2">
            <input
              ref={titleInputRef}
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveTitle();
                if (e.key === 'Escape') cancelEditTitle();
              }}
              onBlur={saveTitle}
              className="flex-1 text-2xl font-bold bg-bg-input border border-border rounded-md px-3 py-1 text-text-primary focus:outline-none focus:border-accent"
            />
            <button onClick={saveTitle} className="p-1 text-success hover:bg-bg-hover rounded">
              <Check size={20} />
            </button>
            <button onClick={cancelEditTitle} className="p-1 text-text-muted hover:bg-bg-hover rounded">
              <X size={20} />
            </button>
          </div>
        ) : (
          <h1
            className="text-2xl font-bold text-text-primary cursor-pointer hover:text-accent transition-colors group flex items-center gap-2"
            onClick={startEditTitle}
          >
            {task.title}
            <Pencil size={16} className="opacity-0 group-hover:opacity-50 transition-opacity" />
          </h1>
        )}
        <div className="flex items-center gap-3 mt-2 text-xs text-text-muted">
          <span>Created {formatRelativeTime(task.created_at)}</span>
          {task.completed_at && <span>Completed {formatRelativeTime(task.completed_at)}</span>}
        </div>
      </div>

      {/* Properties Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 bg-bg-card border border-border rounded-lg p-4">
        {/* Status */}
        <div>
          <label className="block text-xs text-text-muted mb-1.5 uppercase tracking-wide">Status</label>
          <select
            value={task.status}
            onChange={(e) => patchTask({ status: e.target.value })}
            className={`w-full px-3 py-2 bg-bg-input border border-border rounded-md text-sm font-medium ${statusCfg.color} focus:outline-none focus:border-accent cursor-pointer`}
          >
            {STATUS_OPTIONS.map(s => {
              const cfg = STATUS_CONFIG[s];
              return <option key={s} value={s}>{cfg?.label ?? s}</option>;
            })}
          </select>
        </div>

        {/* Priority */}
        <div>
          <label className="block text-xs text-text-muted mb-1.5 uppercase tracking-wide">Priority</label>
          <select
            value={task.priority}
            onChange={(e) => patchTask({ priority: e.target.value })}
            className={`w-full px-3 py-2 bg-bg-input border border-border rounded-md text-sm font-medium ${priorityCfg.color} focus:outline-none focus:border-accent cursor-pointer`}
          >
            {PRIORITY_OPTIONS.map(p => {
              const cfg = PRIORITY_CONFIG[p];
              return <option key={p} value={p}>{cfg?.label ?? p}</option>;
            })}
          </select>
        </div>

        {/* Assignee */}
        <div>
          <label className="block text-xs text-text-muted mb-1.5 uppercase tracking-wide flex items-center gap-1">
            <User size={10} /> Assignee
          </label>
          <select
            value={task.assignee_id ?? ''}
            onChange={(e) => patchTask({ assignee_id: e.target.value || null })}
            className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-sm text-text-primary focus:outline-none focus:border-accent cursor-pointer"
          >
            <option value="">Unassigned</option>
            {sortPeopleWithSelf(people).map(p => <option key={p.id} value={p.id}>{displayName(p)}</option>)}
          </select>
        </div>

        {/* Due Date */}
        <div>
          <label className="block text-xs text-text-muted mb-1.5 uppercase tracking-wide flex items-center gap-1">
            <Calendar size={10} /> Due Date
          </label>
          <input
            type="date"
            value={task.due_date ?? ''}
            onChange={(e) => patchTask({ due_date: e.target.value || null })}
            className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-sm text-text-primary focus:outline-none focus:border-accent cursor-pointer"
          />
        </div>

        {/* Tags */}
        <div className="relative">
          <label className="block text-xs text-text-muted mb-1.5 uppercase tracking-wide flex items-center gap-1">
            <Tag size={10} /> Tags
          </label>
          <div
            className="min-h-[38px] px-3 py-1.5 bg-bg-input border border-border rounded-md flex flex-wrap gap-1.5 items-center cursor-pointer hover:border-border-hover transition-colors"
            onClick={() => setShowTagPicker(!showTagPicker)}
          >
            {task.tag_ids.length === 0 ? (
              <span className="text-sm text-text-muted">Add tags...</span>
            ) : (
              task.tag_ids.map(tid => {
                const tag = tagMap.get(tid);
                return tag ? (
                  <span
                    key={tid}
                    className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
                    style={{ backgroundColor: tag.color + '20', color: tag.color }}
                  >
                    {tag.name}
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleTag(tid); }}
                      className="hover:opacity-70"
                    >
                      <X size={10} />
                    </button>
                  </span>
                ) : null;
              })
            )}
          </div>
          {showTagPicker && (
            <DropdownPicker
              items={tags.map(t => ({ id: t.id, label: t.name, color: t.color }))}
              selectedIds={task.tag_ids}
              onToggle={toggleTag}
              onClose={() => setShowTagPicker(false)}
              onCreate={createTag}
            />
          )}
        </div>

        {/* Scopes */}
        <div className="relative">
          <label className="block text-xs text-text-muted mb-1.5 uppercase tracking-wide flex items-center gap-1">
            <Crosshair size={10} /> Projects
          </label>
          <div
            className="min-h-[38px] px-3 py-1.5 bg-bg-input border border-border rounded-md flex flex-wrap gap-1.5 items-center cursor-pointer hover:border-border-hover transition-colors"
            onClick={() => setShowScopePicker(!showScopePicker)}
          >
            {task.scope_ids.length === 0 ? (
              <span className="text-sm text-text-muted">Add projects...</span>
            ) : (
              task.scope_ids.map(sid => {
                const scope = scopeMap.get(sid);
                return scope ? (
                  <span
                    key={sid}
                    className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1 bg-bg-hover text-text-secondary"
                    style={scope.color ? { backgroundColor: scope.color + '20', color: scope.color } : undefined}
                  >
                    {scope.name}
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleScope(sid); }}
                      className="hover:opacity-70"
                    >
                      <X size={10} />
                    </button>
                  </span>
                ) : null;
              })
            )}
          </div>
          {showScopePicker && (
            <DropdownPicker
              items={scopes.map(s => ({ id: s.id, label: s.name, color: s.color ?? undefined }))}
              selectedIds={task.scope_ids}
              onToggle={toggleScope}
              onClose={() => setShowScopePicker(false)}
              onCreate={createScope}
            />
          )}
        </div>
      </div>

      {/* Linked Entities */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 bg-bg-card border border-border rounded-lg p-4">
        <LinkPicker
          sourceType="task"
          sourceId={task.id}
          relationship="waiting_on"
          allowedTargetTypes={['person']}
          label="Waiting On"
          placeholder="Add person..."
        />
        <LinkPicker
          sourceType="task"
          sourceId={task.id}
          relationship="follow_up_with"
          allowedTargetTypes={['person']}
          label="Follow Up With"
          placeholder="Add person..."
        />
        <LinkPicker
          sourceType="task"
          sourceId={task.id}
          relationship="related"
          allowedTargetTypes={['task', 'meeting_instance', 'meeting_series', 'reference_doc']}
          label="Related Items"
          placeholder="Link task, meeting, doc..."
        />
      </div>

      {/* Description */}
      <div className="mb-8">
        <label className="block text-xs text-text-muted mb-2 uppercase tracking-wide">Description</label>
        <DossierEditor
          content={noteContent}
          onUpdate={handleDescriptionUpdate}
          editable={true}
          placeholder="Add a description..."
        />
      </div>

      {/* Change History */}
      <div>
        <h2 className="flex items-center gap-2 text-sm font-semibold text-text-secondary mb-3 uppercase tracking-wide">
          <History size={14} /> Change History
        </h2>
        {history.length === 0 ? (
          <p className="text-sm text-text-muted py-4">No changes recorded yet.</p>
        ) : (
          <div className="space-y-0">
            {history.map((entry, idx) => (
              <HistoryItem
                key={entry.id}
                entry={entry}
                personMap={personMap}
                isLast={idx === history.length - 1}
              />
            ))}
          </div>
        )}
      </div>
    </div>
    </div>
  );
}

/* ── History Item Component ───────────────────────────── */

function HistoryItem({
  entry,
  personMap,
  isLast,
}: {
  entry: HistoryEntry;
  personMap: Map<string, PersonItem>;
  isLast: boolean;
}) {
  const icon = HISTORY_ICONS[entry.field_name] ?? <Pencil size={12} />;
  const color = HISTORY_COLORS[entry.field_name] ?? 'text-text-muted';

  function formatFieldName(field: string): string {
    return field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  function formatValue(field: string, value: string | null): string {
    if (value === null || value === '') return 'none';
    if (field === 'status') return STATUS_CONFIG[value]?.label ?? value;
    if (field === 'priority') return PRIORITY_CONFIG[value]?.label ?? value;
    if (field === 'assignee_id') return personMap.get(value)?.name ?? value;
    if (field === 'due_date') return formatShortDate(value);
    return value;
  }

  return (
    <div className="flex gap-3">
      {/* Timeline connector */}
      <div className="flex flex-col items-center">
        <div className={`w-6 h-6 rounded-full bg-bg-surface border border-border flex items-center justify-center ${color}`}>
          {icon}
        </div>
        {!isLast && <div className="w-px flex-1 bg-border" />}
      </div>
      {/* Content */}
      <div className="pb-4 flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-text-secondary">{formatFieldName(entry.field_name)}</span>
          <span className="text-text-muted">{formatValue(entry.field_name, entry.old_value)}</span>
          <ArrowRight size={12} className="text-text-muted flex-shrink-0" />
          <span className="text-text-primary font-medium">{formatValue(entry.field_name, entry.new_value)}</span>
        </div>
        <div className="text-xs text-text-muted mt-0.5">
          {formatRelativeTime(entry.changed_at)}
        </div>
      </div>
    </div>
  );
}

/* ── Utility Functions ────────────────────────────────── */


function formatShortDate(d: string): string {
  return new Date(d + (d.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
